import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Music, Pencil, Video } from 'lucide-react'
import { saveRoutine } from '../api/teacher'
import type { ChoreoLink } from '../api/calendar'
import { asChoreoInput, asChoreoRow, choreoFileHref, isChoreoFile } from '../api/choreoLinks'
import { VIDEO_ACCEPT, uploadChoreoVideo, whyNotAVideo } from '../api/choreoUpload'

/**
 * The routine's name and its music, edited by whoever teaches it (D-319).
 *
 * **This lived only in Tonya's admin screen.** Setting a music link required
 * `ManageClasses`, which also rewrites the timetable and the public listing, so
 * no teacher could hold it — and every music folder therefore went through her.
 * Teachers are the people who actually have the music.
 *
 * **Read-only when she may not edit**, rather than absent: seeing the link is
 * the common case and is worth having even for somebody covering a class.
 */
export default function RoutineEditor({
  classId,
  routineName,
  musicUrl,
  choreoLinks,
  canEdit,
}: {
  classId: string
  routineName: string | null
  musicUrl: string | null
  /** The practice videos for families (D-541, D-668), in order: a link she pasted or a file she uploaded. */
  choreoLinks: ChoreoLink[]
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(routineName ?? '')
  const [url, setUrl] = useState(musicUrl ?? '')
  const [links, setLinks] = useState(choreoLinks.map(asChoreoRow))
  const [error, setError] = useState<string | null>(null)

  const picker = useRef<HTMLInputElement>(null)
  const [sent, setSent] = useState(0)

  const queryClient = useQueryClient()

  /**
   * <b>The upload is its own action, not part of Save</b> (D-670).
   *
   * The claim writes the row and rings the bell, so by the time she presses
   * Save the video is already on the routine — Save is then only what it
   * always was, a whole-list replace carrying her labels and her order. Doing
   * it the other way round would mean holding a gigabyte in the browser until
   * she pressed a button, and losing it if she did not.
   */
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const why = whyNotAVideo(file)
      if (why) throw new Error(why)

      setSent(0)
      return uploadChoreoVideo(classId, file, setSent)
    },
    onMutate: () => setError(null),
    onSuccess: (claimed) => {
      /*
       * Added here as well as refetched. The row exists on the server the
       * instant the claim returns, but this form is holding her unsaved edits
       * — so re-reading the prop would either discard them or show a list
       * missing the video she just watched upload.
       */
      setLinks((current) => [
        ...current,
        { label: '', url: '', mediaAssetId: claimed.mediaAssetId },
      ])

      queryClient.invalidateQueries({ queryKey: ['teacher-classes'] })
      queryClient.invalidateQueries({ queryKey: ['roster'] })
    },
    onError: (e: Error) => setError(e.message),
    onSettled: () => setSent(0),
  })

  const save = useMutation({
    mutationFn: () =>
      saveRoutine(
        classId,
        name.trim() || null,
        url.trim() || null,
        links.map(asChoreoInput),
      ),
    onMutate: () => setError(null),
    onSuccess: () => {
      // Both lists show these fields, and a stale one is how a teacher decides
      // the save did not work and does it again.
      queryClient.invalidateQueries({ queryKey: ['teacher-classes'] })
      queryClient.invalidateQueries({ queryKey: ['roster'] })
      setEditing(false)
    },
    onError: (e: Error) => setError(e.message),
  })

  // Opens from what the server last said, never from what was typed last
  // time: the server trims, drops blank rows and folds a repeated address, so
  // the list a teacher sees on her second Edit is the one families see.
  const resetFromServer = () => {
    setName(routineName ?? '')
    setUrl(musicUrl ?? '')
    setLinks(choreoLinks.map(asChoreoRow))
    setError(null)
  }

  const field =
    'w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13.5px] outline-none focus:border-accent focus:bg-surface'

  if (!editing) {
    return (
      <section className="mb-4 rounded-xl border border-line bg-surface-2 px-3.5 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
            Routine
          </span>

          {canEdit && (
            <button
              type="button"
              onClick={() => {
                resetFromServer()
                setEditing(true)
              }}
              className="flex items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
            >
              <Pencil size={12} strokeWidth={2.5} />
              Edit
            </button>
          )}
        </div>

        <p className="mt-1 text-[13.5px] wrap-anywhere">
          {routineName ?? <span className="text-ink-3">No routine name yet</span>}
        </p>

        {musicUrl ? (
          <a
            href={musicUrl}
            target="_blank"
            // Without `noopener` the opened tab can navigate this one.
            rel="noopener noreferrer"
            className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-accent-2 no-underline hover:underline"
          >
            <Music size={14} strokeWidth={2.5} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">Open the music folder</span>
            <ExternalLink size={13} strokeWidth={2} aria-hidden="true" className="shrink-0" />
          </a>
        ) : (
          <p className="mt-2 text-[12.5px] text-ink-3">
            {canEdit ? 'No music link yet — add one so it is here next week.' : 'No music link yet.'}
          </p>
        )}

        {/*
          The practice video (D-541). Families see this same link on their own
          schedule; it is here so the teacher who added it can check it opens.
        */}
        {choreoLinks.length > 0 ? (
          <ul className="mt-1.5 space-y-1">
            {choreoLinks.map((link, i) => (
              <li key={link.mediaAssetId ?? link.url ?? i}>
                <a
                  href={
                    link.mediaAssetId ? choreoFileHref(classId, link.mediaAssetId) : (link.url ?? '')
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[13px] font-semibold text-accent-2 no-underline hover:underline"
                >
                  <Video size={14} strokeWidth={2.5} className="shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{link.label ?? `Practice video ${i + 1}`}</span>
                  <ExternalLink size={13} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1.5 text-[12.5px] text-ink-3">
            {canEdit ? 'No practice videos yet — families see them on their schedule.' : 'No practice videos yet.'}
          </p>
        )}
      </section>
    )
  }

  return (
    <section className="mb-4 rounded-xl border border-accent bg-surface-2 px-3.5 py-3">
      <span className="text-[12px] font-bold tracking-[0.11em] text-accent uppercase">Routine</span>

      <label className="mt-2 block">
        <span className="mb-1 block text-[12.5px] font-semibold text-ink-2">Name of the dance</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Rise Up"
          className={field}
        />
      </label>

      <label className="mt-2.5 block">
        <span className="mb-1 block text-[12.5px] font-semibold text-ink-2">Music link</span>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://drive.google.com/..."
          // `url` rather than `text`, so a phone offers the right keyboard.
          type="url"
          inputMode="url"
          className={field}
        />
        <span className="mt-1 block text-[11.5px] text-ink-3">
          Paste the whole address, starting with https://
        </span>
      </label>

      {/*
        Practice videos (D-541): one row per part of the routine, in the order
        it goes. The one word that matters is under the list — a YouTube video
        set to Private cannot be watched from a link by anyone, and a family's
        "video unavailable" would be the first the teacher heard of it.
      */}
      <fieldset className="mt-2.5">
        <legend className="mb-1 block text-[12.5px] font-semibold text-ink-2">Practice videos</legend>
        <div className="space-y-2">
          {links.map((row, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input
                value={row.label}
                onChange={(e) => setLinks(links.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))}
                placeholder={`Part ${i + 1}`}
                aria-label={`Label for practice video ${i + 1}`}
                className={`${field} basis-32 grow-0`}
              />
              {/*
                An uploaded video has no address to edit (D-668), so the row
                says what it is instead of offering an empty box that would
                read as a link somebody forgot to paste. It is still hers to
                label and to remove.
              */}
              {isChoreoFile(row) ? (
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[12.5px] font-semibold text-ink-2">
                  <Video size={13} strokeWidth={2.5} className="shrink-0" />
                  Uploaded video
                </span>
              ) : (
                <input
                  value={row.url}
                  onChange={(e) => setLinks(links.map((l, j) => (j === i ? { ...l, url: e.target.value } : l)))}
                  placeholder="https://youtu.be/..."
                  type="url"
                  inputMode="url"
                  aria-label={`Address of practice video ${i + 1}`}
                  className={`${field} min-w-0 flex-1`}
                />
              )}
              <button
                type="button"
                onClick={() => setLinks(links.filter((_, j) => j !== i))}
                aria-label={`Remove practice video ${i + 1}`}
                className="min-h-[36px] rounded-lg border border-line-2 px-2.5 text-[12.5px] font-semibold text-ink-2 hover:border-crit hover:text-crit"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setLinks([...links, { label: '', url: '', mediaAssetId: null }])}
            className="min-h-[36px] rounded-lg border border-line-2 px-2.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
          >
            {links.length === 0 ? 'Add a link' : 'Add another link'}
          </button>

          {/*
            <b>The file half</b> (D-670). A hidden input behind a real button,
            the same shape the photograph library uses — a native file input
            cannot be styled and reads as a different application.
          */}
          <input
            ref={picker}
            type="file"
            accept={VIDEO_ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0]
              // Cleared so choosing the same file twice still fires a change.
              e.target.value = ''
              if (file) upload.mutate(file)
            }}
          />

          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => picker.current?.click()}
            className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-line-2 px-2.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2 disabled:opacity-60"
          >
            <Video size={13} strokeWidth={2.5} />
            {upload.isPending ? 'Uploading...' : 'Upload a video'}
          </button>
        </div>

        {/*
          <b>A real bar, from a real byte count.</b> A phone sending 240 MB over
          studio wifi with no progress is indistinguishable from a phone that
          has frozen, and that is the moment a teacher force-quits and tries
          again. It is the reason this one upload uses XMLHttpRequest.
        */}
        {upload.isPending && (
          <div className="mt-2">
            <div
              className="h-1.5 overflow-hidden rounded-full bg-surface"
              role="progressbar"
              aria-label="Upload progress"
              aria-valuenow={Math.round(sent * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-accent transition-[width]"
                style={{ width: `${Math.round(sent * 100)}%` }}
              />
            </div>
            <span className="mt-1 block text-[11.5px] text-ink-3">
              {Math.round(sent * 100)}% sent &mdash; keep this screen open.
            </span>
          </div>
        )}

        <span className="mt-1 block text-[11.5px] text-ink-3">
          Upload straight from your phone, or paste a YouTube link &mdash; set each
          video to <strong>Unlisted</strong>, not Private.
        </span>
      </fieldset>

      {error && (
        <p role="alert" className="mt-2 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      {/*
        <b>Neither button while a video is still going up</b> (D-672).

        A save sends the whole list and the server replaces it, and the row for
        an upload in flight does not exist yet — so Save pressed at 99% archives
        the video the moment it lands, silently, after the families have already
        been told about it. The window is not milliseconds: the claim commits
        the row and then messages every family in the class before it answers,
        so the bar reads "100% sent" for seconds with Save sitting there live.
        This is D-643's loss, on the same list, reached a different way.
      */}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => {
            // Back to what the server last said, not to whatever was typed —
            // Cancel that keeps the edits is a Cancel that did nothing.
            resetFromServer()
            setEditing(false)
          }}
          className="rounded-lg border border-line-2 px-3 py-1.5 text-[13px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-60"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={save.isPending || upload.isPending}
          onClick={() => save.mutate()}
          className="flex-1 rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-60"
        >
          {/*
            Still "Save" while an upload runs, not a second "Uploading...".
            The upload button says that, and the bar above says how far — two
            controls claiming the same action is worse than one disabled one.
          */}
          {save.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  )
}
